import { MFChannel } from '../../../Components/SDK';
import { getMessageById, updateMediaStatus } from '../../../slice';
import { Store } from '../../../store';
import logger from '../../../Helpers/logger';
import mediaStorage from './MediaStorage';

const uploadQueues = {};
const downloadQueue = [];
let isProcessingDownloadQueue = false;

const enqueueDownloadTask = (messageId, task) =>
    new Promise((resolve, reject) => {
        downloadQueue.push({ messageId, task, resolve, reject });
        processDownloadQueue();
    });

const processDownloadQueue = async () => {
    if (isProcessingDownloadQueue) return;
    const next = downloadQueue[0];
    if (!next) {
        return;
    }

    const { task, resolve, reject } = next;

    isProcessingDownloadQueue = true;

    try {
        const result = await task();
        resolve(result);
    } catch (error) {
        reject(error);
    } finally {
        downloadQueue.shift();
        isProcessingDownloadQueue = false;
        processDownloadQueue();
    }
};

/**
 * Enqueue a file upload
 */
export function enqueueFile(messageObject, channelId, fileOptions, msgType, isResume = false) {
    if (!uploadQueues[channelId]) uploadQueues[channelId] = [];
    uploadQueues[channelId].push({ messageObject, fileOptions, msgType, isResume });
    processQueue(channelId);
}

/**
 * Process next file in the queue
 */
async function processQueue(channelId) {
    const queue = uploadQueues[channelId];
    if (!queue || queue.length === 0) return;

    const current = queue[0];
    const { messageObject, fileOptions, msgType, isResume } = current;

    const status = messageObject.media?.isUploading;
    if (status === 1 || status === 2) return; // already uploading/done

    try {
        Store.dispatch(
            updateMediaStatus({
                channelId,
                messageId: messageObject.messageId,
                is_uploading: 1,
            }),
        );

        let result;

        if (isResume) {
            result = await MFChannel.resumeMediaUpload(messageObject.messageId);
        } else {
            try {
                delete fileOptions?.file?.fileDetails;
                delete fileOptions?.file?.caption;
                delete fileOptions?.file?.mentionedUsersIds;
            } catch (error) {}

            result = await MFChannel.sendMessage(fileOptions, msgType);
        }

        logger.debug('processQueue result ==>', result);

        if (result?.statusCode === 200) {
            if (msgType === 'image' && !isResume) {
                await mediaStorage.save(messageObject.messageId, fileOptions.file);
            }

            Store.dispatch(
                updateMediaStatus({
                    channelId,
                    messageId: messageObject.messageId,
                    is_uploading: 2,
                }),
            );
        } else {
            Store.dispatch(
                updateMediaStatus({
                    channelId,
                    messageId: messageObject.messageId,
                    is_uploading: 3,
                }),
            );
        }

        queue.shift();
        processQueue(channelId);
    } catch (error) {
        logger.error('Upload error:', error);

        Store.dispatch(
            updateMediaStatus({
                channelId,
                messageId: messageObject.messageId,
                is_uploading: 1, // failed
            }),
        );
    }
}

export const downloadMediaAndTriggerSave = (channelId, messageId) =>
    enqueueDownloadTask(messageId, async () => {
        try {
            let blob = null;
            const { content: { media: { fileName } = {} } = {} } = getMessageById(channelId, messageId) || {};
            const existing = await mediaStorage.get(messageId);
            if (existing?.blob) {
                blob = existing.blob;
            } else {
                Store.dispatch(updateMediaStatus({ channelId, messageId, is_downloaded: 1 }));
                const res = await MFChannel.downloadMediaFile(channelId, messageId);
                if (res?.blob) {
                    blob = res.blob;
                    Store.dispatch(
                        updateMediaStatus({
                            channelId,
                            messageId,
                            local_path: window.URL.createObjectURL(res.blob),
                            is_downloaded: 2,
                            is_uploading: 2,
                        }),
                    );
                } else {
                    Store.dispatch(updateMediaStatus({ channelId, messageId, is_downloaded: 0, is_uploading: 2 }));
                }
            }
            if (!blob) throw new Error('Failed to download media');

            const fileUrl = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.style.display = 'none';
            anchor.href = fileUrl;
            anchor.setAttribute('download', fileName);
            document.body.appendChild(anchor);
            anchor.click();
            window.URL.revokeObjectURL(fileUrl);
            document.body.removeChild(anchor);
            return { statusCode: 200, message: 'Download triggered successfully' };
        } catch (err) {
            logger.error('Download error:', err);
            return { statusCode: 500, error: err.message };
        }
    });

/**
 * Handle media actions: download, cancel/resume upload/download
 */
export async function handleMediaAction(messageObject, actionType, fileSave) {
    const { messageId, media, channelId } = messageObject;
    switch (actionType) {
        case 'download': {
            return enqueueDownloadTask(messageId, async () => {
                const existing = await mediaStorage.get(messageId);
                if (existing?.blob) {
                    return { statusCode: 200, local_path: window.URL.createObjectURL(existing.blob) };
                }

                const res = await MFChannel.downloadMediaFile(channelId, messageId);
                if (res?.blob) {
                    if (fileSave) {
                        await mediaStorage.save(messageId, res.blob);
                    }
                    return { ...res, local_path: window.URL.createObjectURL(res.blob) };
                }
                return { ...res };
            });
        }

        case 'cancelUpload': {
            await MFChannel.cancelMediaUpload(messageId);
            Store.dispatch(
                updateMediaStatus({
                    channelId,
                    messageId,
                    is_uploading: 3,
                }),
            );
            break;
        }

        case 'resumeUpload': {
            enqueueFile(messageObject, channelId, { fileDetails: media }, messageObject.messageType);
            break;
        }

        case 'cancelDownload': {
            MFChannel.cancelMediaDownload(messageId);
            break;
        }

        default:
            logger.debug(`Unknown action: ${actionType}`);
    }
}
